import { Outlet, useLocation, useMatch, useNavigate } from "react-router-dom";
import NavBar from "../navbar/NavBar";
import { useDispatch, useSelector } from "react-redux";
import {
  clearIntendedPath,
  setIntendedPath,
  setTheme,
  setUserLogin,
  setUserDetails,
} from "@src/app/globalSlice";
import { decryptData } from "@src/utils/crypto";
import { RootState } from "@src/app/store";
import RegisterModal from "./RegisterModal";
import LoginModal from "./LoginModal";
import { useEffect, useState } from "react";
import { USER_CONSTANTS } from "@src/constants/UiConstants";
import { Toastify } from "top-brains-ui";
import { setSelectedMenu } from "@src/app/headersSlice";
import { openExternalUrl } from "@src/services/mediaRuntime";
import { isElectronApp } from "@src/utils/envUtils";
import NavBarElectron from "../navbar/NavBarElectron";

const themePalette = {
  website: {
    textPrimary: "#f0f0f5",
    textSecondary: "#8b8ba0",
    border: "rgba(46, 46, 64, 0.5)",
    footerBg: "rgba(26, 26, 36, 0.88)",
  },
  app: {
    textPrimary: "#1a1a2e",
    textSecondary: "#6b7280",
    border: "rgba(229, 229, 239, 0.8)",
    footerBg: "rgba(255, 255, 255, 0.88)",
  },
};

const LoginLayout = () => {
  const location = useLocation();
  const isLoginModalOpen = location.pathname;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const accessToken = localStorage.getItem("accessToken");
  const isAuthenticated = !!accessToken;
  const isLogin = useSelector((state: RootState) => state.globalSlice.isLogin);
  const userRole = useSelector(
    (state: RootState) => state.globalSlice.userDetails?.currentPrivilege,
  );
  const currentTheme = useSelector(
    (state: RootState) => state.globalSlice.theme,
  );
  const userDetails = useSelector(
    (state: RootState) => state.globalSlice.userDetails,
  );
   const isElectron = isElectronApp();

  const [localIP, setLocalIP] = useState<string>("");

  useEffect(() => {
    if (isElectron && isAuthenticated) {
      const fetchIP = async () => {
        try {
          const info = await (window as any).electronApp.getIPInfo();
          if (info && info.localIP) {
            setLocalIP(info.localIP);
          }
        } catch (e) {
          console.error("Failed to fetch local IP info:", e);
        }
      };
      fetchIP();

      // Listen for network reconnect/online events to recheck IP
      window.addEventListener("online", fetchIP);
      return () => {
        window.removeEventListener("online", fetchIP);
      };
    }
  }, [isElectron, isAuthenticated]);
  const privileges = useSelector(
    (state: RootState) => state.globalSlice.userDetails?.privileges,
  );
  const organizationType = useSelector(
    (state: RootState) => state.globalSlice.userDetails?.organizationType,
  );
  const organizationName = useSelector(
    (state: RootState) => state.globalSlice.userDetails?.organizationName,
  );

  const matchQuestion = useMatch("/challenges/:category/question");
  const matchTopicQuestion = useMatch("/challenges/:category/topic/:question");
  const matchCompanyQuestion = useMatch(
    "/challenges/:category/company/:question",
  );
  const matchSharedQuestion = useMatch("/shared-question/:questionId");
  const matchListQuestion = useMatch("/challenges/:category/list/:question");
  const matchQuiz = useMatch("/website-quiz/quiz-screen");
  const matchTournament = useMatch("/website-tournaments/tournament-screen");
  const matchTournamentInstruction = useMatch(
    "/website-tournaments/tournament-instruction",
  );
  const matchQuizInstruction = useMatch("/website-quiz/instruction");
  const matchTournamentResultDetails = useMatch(
    "/website-tournaments/tournamentDetails",
  );
  const matchTournamentResultDetailsQuestionDetails = useMatch(
    "/website-tournaments/tournamentDetails/questionDetails",
  );
  const matchTournamentResult = useMatch(
    "/website-tournaments/tournament-screen/tournamentResult",
  );
  const matchTournamentResultQuestionDetails = useMatch(
    "/website-tournaments/tournament-screen/tournamentResult/questionDetails",
  );
  const matchQuizResult = useMatch("/website-quiz/quiz-screen/result");
  const matchQuizDetails = useMatch("/website-quiz/details");
  const matchTournamentLeaderboard = useMatch(
    "/website-tournaments/tournament-screen/leaderBoard",
  );

  const isChallengeQuestionPage =
    matchQuestion ||
    matchTopicQuestion ||
    matchCompanyQuestion ||
    matchListQuestion ||
    matchSharedQuestion ||
    (matchQuiz &&
      !matchQuizResult &&
      !matchQuizDetails &&
      !matchQuizInstruction) ||
    (matchTournament &&
      !matchTournamentInstruction &&
      !matchTournamentResult &&
      !matchTournamentResultDetails &&
      !matchTournamentLeaderboard &&
      !matchTournamentResultQuestionDetails &&
      !matchTournamentResultDetailsQuestionDetails);

  const handleMyOrgsNavigation = () => {
    if (location.pathname === "/home") {
      navigate("/home", { state: { scrollTo: "my-orgs" } });
    } else {
      if (isAuthenticated) {
        if (isElectron) {
          const hasMultipleRoles = privileges?.length > 1;
          const includesStudent = privileges?.includes(USER_CONSTANTS.STUDENT);

          if (hasMultipleRoles && includesStudent) {
            navigate("/choose-role");
            return;
          }

          if (hasMultipleRoles && !includesStudent) {
            navigate("/multi_roles");
            return;
          }

          dispatch(setUserLogin(false));
        }

        switch (userRole) {
          case USER_CONSTANTS.STUDENT:
            if (isElectron) {
              const agreed = userDetails?.agreedTermsAndCondition ?? userDetails?.user?.agreedTermsAndCondition;
              if (agreed !== true) {
                navigate("/terms-and-conditions");
              } else {
                navigate("/learn");
              }
            } else {
              navigate("/student-organization");
            }
            dispatch(setUserLogin(false));
            break;
          case USER_CONSTANTS.TASK_MANAGER:
            dispatch(setTheme("app-theme"));
            navigate("/quiz");
            break;
          case USER_CONSTANTS.SUPER_TASK_MANAGER:
            dispatch(setTheme("app-theme"));
            navigate("/organizations");
            break;
          case USER_CONSTANTS.ADMIN:
            if (isElectron) {
              navigate("/subjects");
            } else {
              dispatch(setTheme("app-theme"));
              if (organizationType === "SUB_ORGANIZATION") {
                dispatch(setSelectedMenu(organizationName));
                navigate("/organizations/sub-organization/branch");
              } else if (organizationType === "BRANCH") {
                dispatch(setSelectedMenu(organizationName));
                navigate("/organizations/branch/branch-dashboard");
              } else {
                navigate("/subjects");
              }
            }
            break;
          default:
            dispatch(setTheme("app-theme"));
            navigate("/subjects");
        }
      } else {
        dispatch(
          setIntendedPath({
            key: "my-orgs",
            path: "__my-orgs__",
          }),
        );
        dispatch(setUserLogin(true));
      }
    }
  };

  const scrollOrNavigate = (scrollTarget: string, authPath: string) => {
    if (location.pathname === "/home") {
      navigate("/home", { state: { scrollTo: scrollTarget } });
    } else {
      if (isAuthenticated) {
        navigate(authPath);
        dispatch(setTheme("website-theme"));
      } else {
        dispatch(
          setIntendedPath({
            key: scrollTarget,
            path: authPath,
          }),
        );
        dispatch(setTheme("website-theme"));
        dispatch(setUserLogin(true));
      }
    }
  };

  const isStudent =
    userRole?.toLowerCase?.() === USER_CONSTANTS.STUDENT.toLowerCase();

  const websiteNavLinks = [
    {
      label: "Challenges",
      onClick: () => {
        navigate("/home", { state: { scrollTo: "programming-challenges" } });
      },
    },
    {
      label: "Quiz",
      onClick: () => scrollOrNavigate("quiz", "/website-quiz"),
    },
    {
      label: "Tournaments",
      onClick: () => scrollOrNavigate("tournament", "/website-tournaments"),
    },
    {
      label: "My Org's",
      onClick: handleMyOrgsNavigation,
    },
  ];

  const electronNavLinks =
    isAuthenticated && isStudent
      ? [
          {
            label: "Learn",
            onClick: () => {
              const agreed = userDetails?.agreedTermsAndCondition ?? userDetails?.user?.agreedTermsAndCondition;
              if (agreed !== true) {
                navigate("/terms-and-conditions");
              } else {
                navigate("/learn");
              }
            },
          },
          // {
          //   label: "Practice",
          //   onClick: () => openExternalUrl("https://topbrains.com/"),
          // },
        ]
      : [];

  const navLinks = isElectron ? electronNavLinks : websiteNavLinks;

  useEffect(() => {
    if (isElectron) {
      if (isAuthenticated) {
        return;
      }

      const isStudentTheme =
        currentTheme === "website-theme" || currentTheme === "app-theme";

      if (isAuthenticated && isStudent && isStudentTheme) {
        return;
      }
    } else {
      if (
        currentTheme !== "website-theme" &&
        currentTheme !== "website-light-theme"
      ) {
        dispatch(setTheme("website-theme"));
      }
    }
  }, [dispatch, currentTheme, isAuthenticated, userRole]);

  // Load userDetails on mount if present in localStorage but not in Redux
  useEffect(() => {
    const encryptedUser = localStorage.getItem("userDetails");
    if (encryptedUser && !userDetails) {
      try {
        const userData = decryptData(encryptedUser);
        if (userData) {
          dispatch(setUserDetails(userData));
        }
      } catch (err) {
        console.error("Failed to decrypt userDetails on startup:", err);
        localStorage.removeItem("userDetails");
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
      }
    }
  }, [userDetails, dispatch]);

  // Guard routing based on terms agreement status
  useEffect(() => {
    if (isElectron && isAuthenticated && isStudent && userDetails) {
      const agreed = userDetails?.agreedTermsAndCondition ?? userDetails?.user?.agreedTermsAndCondition;
      if (agreed !== true) {
        if (location.pathname !== "/terms-and-conditions") {
          navigate("/terms-and-conditions");
        }
      } else {
        if (location.pathname === "/terms-and-conditions") {
          navigate("/learn");
        }
      }
    }
  }, [isElectron, isAuthenticated, isStudent, userDetails, location.pathname, navigate]);
  
  const isLoginRoute = useMatch("login");
  const isLandingRoute = useMatch("/");
  const isLearn = useMatch("learn");
  const isLearnSubjects = useMatch("learn-subjects");
  const isMultipleRole = useMatch("choose-role");
  const isMultiRoles = useMatch("multi_roles");
  const matchLandingPortal = useMatch("landing-portal");
  const matchPortal = useMatch("portal");
  const isLandingPortal = matchLandingPortal || matchPortal;
  const isAssessmentInstruction = useMatch("/batches/test/instruction");
  const shouldShowNavBar =
    isLandingRoute ||
    isLandingPortal ||
    isLearnSubjects ||
    isLearn ||
    isChallengeQuestionPage ||
    isMultipleRole ||
    isMultiRoles ||
    isAssessmentInstruction;

  return (
    <>
      {isElectron ? (
        <div>
          {shouldShowNavBar && (
            <NavBarElectron links={navLinks} showLinks={true} />
          )}
        </div>
      ) : (
        <div
          style={{
            display: isChallengeQuestionPage ? "none" : "block",
            position: "sticky",
            top: 0,
            zIndex: 1000,
          }}
        >
          <NavBar links={navLinks} showLinks={true} />
        </div>
      )}
      <Outlet />
      {isLoginModalOpen === "/login" && !isElectron ? (
        <LoginModal modelType="login" />
      ) : isLoginModalOpen === "/signup" ? (
        <RegisterModal modelType="signup" />
      ) : isLoginModalOpen === "/registration-success" ? (
        <RegisterModal modelType="registration-success" />
      ) : isLoginModalOpen === "/forgot-password" ? (
        <RegisterModal modelType="forgot-password" />
      ) : isLoginModalOpen === "/password-success" ? (
        <RegisterModal modelType="password-success" />
      ) : isLoginModalOpen === "/link-expired" ? (
        <RegisterModal modelType="link-expired" />
      ) : isLoginModalOpen === "/set-password-link-expired" ? (
        <RegisterModal modelType="set-password-link-expired" />
      ) : isLoginModalOpen === "/reset-password-link-expired" ? (
        <RegisterModal modelType="reset-password-link-expired" />
      ) : isLoginModalOpen === "/resent-password-link" ? (
        <RegisterModal modelType="resent-password-link" />
      ) : isLoginModalOpen.includes("/set-password/") ? (
        <RegisterModal modelType="set-password" />
      ) : isLoginModalOpen.includes("/reset-password/") ? (
        <RegisterModal modelType="reset-password" />
      ) : isLoginModalOpen.includes("/change-password") ? (
        <RegisterModal modelType="change-password" />
      ) : null}
      {isLogin && (
        <LoginModal
          onClose={() => {
            dispatch(setUserLogin(false));
            dispatch(clearIntendedPath());
          }}
        />
      )}
      <Toastify />{" "}
      {isElectron && isAuthenticated && userDetails && (() => {
        const isDark = currentTheme !== "app-theme" && currentTheme !== "website-light-theme";
        const activeTheme = isDark ? themePalette.website : themePalette.app;

        return (
          <div
            style={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              height: "26px",
              backgroundColor: activeTheme.footerBg,
              backdropFilter: "blur(8px)",
              borderTop: `1px solid ${activeTheme.border}`,
              color: activeTheme.textPrimary,
              fontSize: "11px",
              fontFamily: "'Poppins', sans-serif",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "0 16px",
              zIndex: 999999,
              pointerEvents: "none",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span style={{ color: activeTheme.textSecondary }}>Candidate:</span>
              <span style={{ fontWeight: 600 }}>{userDetails?.name || userDetails?.username || "Unknown"}</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span style={{ color: activeTheme.textSecondary }}>Local IP:</span>
              <span style={{ fontWeight: 600 }}>{localIP || "Detecting..."}</span>
            </div>
          </div>
        );
      })()}
    </>
  );
};

export default LoginLayout;
